import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  CircularProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Global } from '@emotion/react';
import { useNavigate } from 'react-router-dom';
import IELTSBottomNavigation from './IELTSBottomNavigation';
import IELTSHeader from './IELTSHeader';
import IELTSBandScore from './IELTSBandScore';
import IELTSDetailedScoring from './IELTSDetailedScoring';
import SubscriptionAiPromptDialog from './SubscriptionAiPromptDialog';
import EndModal from '../MockTest/Common/EndModal';
import { Base_URL } from './constants/api';
import { formatTime } from './utils/timeHelpers';
import { hasAIScore, hasScore, shouldShowScore, getAIScoring } from './utils/scoreHelpers';
import { cleanQuestionText, countWords } from './utils/textHelpers';
import { QuestionsContainer } from './styles/componentStyles';
import useIeltsAutosave from './hooks/useIeltsAutosave';
import {
  ieltsPassageGlobalStyles,
  ieltsPassageInlineStyles,
} from './styles/passageStyles';
import { themeColors } from './styles/theme';

// Thin scrollbar (default colors) + subtle focus for writing textarea
const writingTextareaStyles = `
  .ielts-writing-textarea::-webkit-scrollbar { width: 8px; }
  .ielts-writing-textarea::-webkit-scrollbar-track { background: ${themeColors.scrollbarTrack}; border-radius: 8px; }
  .ielts-writing-textarea::-webkit-scrollbar-thumb { background: ${themeColors.scrollbarThumb}; border-radius: 8px; }
  .ielts-writing-textarea::-webkit-scrollbar-thumb:hover { background: ${themeColors.scrollbarThumbHover}; }
  .ielts-writing-textarea { scrollbar-width: thin; scrollbar-color: ${themeColors.scrollbarThumb} ${themeColors.scrollbarTrack}; }
  .ielts-writing-textarea:focus { outline: none; box-shadow: none; }
`;

// IELTS Writing-specific messages for EndModal
const IELTS_WRITING_SENTENCES = [
  "Processing your writing responses...",
  "Analyzing Task 1...",
  "Evaluating Task 2...",
  "Scoring grammar and vocabulary...",
  "Finalizing your writing test results..."
];

const TestContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  padding: 0,
  height: '100vh',
  width: '100vw',
  maxWidth: '100vw',
  overflow: 'hidden',
  boxSizing: 'border-box',
}));


// Styled PassageContainer for writing tests (extends QuestionsContainer)
const PassageContainer = styled(QuestionsContainer)(({ theme }) => ({
  paddingBottom: '77px',
}));

// Styled QuestionsContainer for writing answers
const WritingQuestionsContainer = styled(QuestionsContainer)(({ theme }) => ({
  paddingBottom: '77px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  justifyContent: 'flex-start',
}));

const DraggableDivider = styled(Box)(({ theme, dragging }) => ({
  width: '6px',
  cursor: 'col-resize',
  background: dragging ? '#C7B6FF' : '#E0E0E0',
  transition: 'background 0.2s',
  zIndex: 2,
  borderRadius: '4px',
  margin: '0 2px',
  alignSelf: 'stretch',
}));


const WritingTestUI = ({ mode = 'practice', timeLimit, onComplete, sections = [], selectedSectionIndices, groupId, fullTestAttemptId, showSaveExit = false, apiResult: apiResultProp, testType, testId, onSubmitStart, onFullTestPause, requiresSubscriptionForAiScoring = false }) => {
  const navigate = useNavigate();
  const isTestMode = mode === 'test' || mode === 'review';
  const isReviewMode = mode === 'review';
  
  const [currentSection, setCurrentSection] = useState(0); // index
  const [timer, setTimer] = useState({
    minutes: timeLimit === 'Unlimited' ? 'Unlimited' : (timeLimit || 60),
    seconds: 0
  });
  const [isTimerRunning, setIsTimerRunning] = useState(!isReviewMode);
  const [answers, setAnswers] = useState(() => sections.map(() => ''));
  const [userAnswers, setUserAnswers] = useState({}); // For review mode
  const [leftWidth, setLeftWidth] = useState(50); // percent
  const [dragging, setDragging] = useState(false);
  const containerRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiResult, setApiResult] = useState(null);
  const [testStartTime] = useState(() => new Date().toISOString().slice(0, 19).replace('T', ' '));
  const [testEndTime, setTestEndTime] = useState(null);
  const [endModalOpen, setEndModalOpen] = useState(false);
  const [subscriptionPromptOpen, setSubscriptionPromptOpen] = useState(false);
  const draftTestId = testId || sections[0]?.test_id || sections[0]?.testId || sections[0]?.test?.id;
  const draftIdentity = useMemo(() => ({
    scope: fullTestAttemptId ? 'full' : 'sectional',
    section: 'writing',
    testId: draftTestId,
    groupId,
    fullTestAttemptId,
  }), [draftTestId, fullTestAttemptId, groupId]);
  const writingDraftState = useMemo(() => ({
    answers,
    currentStep: { currentSection, mode, selectedSectionIndices },
    timeState: { timer },
    audioState: {},
  }), [answers, currentSection, mode, selectedSectionIndices, timer]);
  const restoreWritingDraft = useCallback((draft) => {
    if (Array.isArray(draft?.answers)) {
      setAnswers((prev) => sections.map((_, idx) => draft.answers[idx] ?? prev[idx] ?? ''));
    }
    if (Number.isInteger(draft?.currentStep?.currentSection)) {
      setCurrentSection(Math.max(0, Math.min(draft.currentStep.currentSection, Math.max(sections.length - 1, 0))));
    }
    if (draft?.timeState?.timer && !isReviewMode) {
      setTimer(draft.timeState.timer);
    }
  }, [isReviewMode, sections]);
  const { clearDraft } = useIeltsAutosave({
    enabled: !isReviewMode && Boolean(draftTestId),
    identity: draftIdentity,
    draftState: writingDraftState,
    onRestore: restoreWritingDraft,
  });
  
  // Set apiResult from prop when in review mode
  useEffect(() => {
    if (apiResultProp) {
      setApiResult(apiResultProp);
    }
  }, [apiResultProp]);
  
  // Stop timer in review mode
  useEffect(() => {
    if (isReviewMode) {
      setIsTimerRunning(false);
    }
  }, [isReviewMode]);
  
  // Extract user responses from apiResult in review mode
  useEffect(() => {
    if (isReviewMode && apiResultProp && apiResultProp.response && apiResultProp.response.analytics) {
      const analytics = apiResultProp.response.analytics;
      const extractedAnswers = {};
      
      // Extract part1 and part2 responses
      if (analytics.part1) {
        extractedAnswers[0] = analytics.part1.responseHtml || analytics.part1.userAnswer || '';
      }
      if (analytics.part2) {
        extractedAnswers[1] = analytics.part2.responseHtml || analytics.part2.userAnswer || '';
      }
      
      setUserAnswers(extractedAnswers);
    }
  }, [isReviewMode, apiResultProp]);
  
  
  // Check if score is available (not pending) - handles 0 correctly
  const scoreAvailable = hasScore(apiResultProp);

  // Handle submit
  const handleSubmit = useCallback(async () => {
    if (isSubmitting) return;
    if (requiresSubscriptionForAiScoring && !isTestMode) {
      setSubscriptionPromptOpen(true);
      return;
    }
    setIsTimerRunning(false);
    setIsSubmitting(true);
    
    // Call onSubmitStart immediately if provided (for full test navigation)
    if (onSubmitStart) {
      onSubmitStart();
    }
    
    // Show EndModal for practice/simulation mode (not full test mode)
    if (!isTestMode) {
      setEndModalOpen(true);
    }
    
    // Set end time when submitting
    const endTime = new Date().toISOString().slice(0, 19).replace('T', ' ');
    setTestEndTime(endTime);
    
    // Extract test_type from sections or use prop
    const test_type = testType || sections[0]?.test_type || sections[0]?.test?.test_type || 'Academic';
    
    // Build payload
    const payload = {
      testId: sections[0]?.test_id || sections[0]?.testId || 1,
      test_type: test_type,
      start_time: testStartTime,
      end_time: endTime,
      ...(groupId ? { groupId } : {}),
      ...(groupId && fullTestAttemptId ? { fullTestAttemptId } : {}),
      ...(groupId ? { is_speaking_optional: localStorage.getItem('is_speaking_optional') === 'true' } : {}),
    };
    if (sections[0]) {
      // Get question ID from the first question in the section, or use partId as fallback
      const questionId = sections[0].questions && sections[0].questions.length > 0 
        ? sections[0].questions[0].id 
        : null;
      payload.part1 = {
        partId: sections[0].id,
        questionId: questionId, // Include question ID from the test data
        userAnswer: answers[0] || '',
        questionText: cleanQuestionText(sections[0].passage_text || ''),
      };
    }
    if (sections[1]) {
      // Get question ID from the first question in the section, or use partId as fallback
      const questionId = sections[1].questions && sections[1].questions.length > 0 
        ? sections[1].questions[0].id 
        : null;
      payload.part2 = {
        partId: sections[1].id,
        questionId: questionId, // Include question ID from the test data
        userAnswer: answers[1] || '',
        questionText: cleanQuestionText(sections[1].passage_text || ''),
      };
    }
    try {
      const response = await fetch(`${Base_URL}/app/users/ielts-tests/saveWritingTestAttempt`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      setIsSubmitting(false);
      setApiResult(data);
      clearDraft().catch(() => {});
      
      // Close modal if results are available
      if (hasAIScore(data) || shouldShowScore(data)) {
        setEndModalOpen(false);
      }
      
      if (onComplete) onComplete(data);
    } catch (error) {
      setIsSubmitting(false);
      setEndModalOpen(false);
      console.error('Error submitting writing test:', error);
    }
  }, [isSubmitting, requiresSubscriptionForAiScoring, isTestMode, sections, answers, testStartTime, groupId, fullTestAttemptId, onComplete, testType, onSubmitStart, clearDraft]);

  // Timer logic
  useEffect(() => {
    let interval;
    if (isTimerRunning && timer.minutes !== 'Unlimited') {
      interval = setInterval(() => {
        setTimer(prevTimer => {
          if (prevTimer.minutes === 0 && prevTimer.seconds === 0) {
            clearInterval(interval);
            handleSubmit();
            return prevTimer;
          }
          if (prevTimer.seconds === 0) {
            return { minutes: prevTimer.minutes - 1, seconds: 59 };
          } else {
            return { ...prevTimer, seconds: prevTimer.seconds - 1 };
          }
        });
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isTimerRunning, timer.minutes, handleSubmit]);

  // Draggable divider logic
  const handleMouseDown = (e) => {
    setDragging(true);
  };
  useEffect(() => {
    if (!dragging) return;
    const handleMouseMove = (e) => {
      if (!containerRef.current) return;
      const bounds = containerRef.current.getBoundingClientRect();
      let percent = ((e.clientX - bounds.left) / bounds.width) * 100;
      if (percent < 15) percent = 15;
      if (percent > 85) percent = 85;
      setLeftWidth(percent);
    };
    const handleMouseUp = () => setDragging(false);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragging]);
  useEffect(() => {
    if (dragging) {
      document.body.style.userSelect = 'none';
    } else {
      document.body.style.userSelect = '';
    }
    return () => {
      document.body.style.userSelect = '';
    };
  }, [dragging]);

  // Handle answer change
  const handleAnswerChange = (idx, value) => {
    setAnswers(prev => {
      const updated = [...prev];
      updated[idx] = value;
      return updated;
    });
  };

  // Handle save and exit - navigates to dashboard
  const handleSaveAndExit = () => {
    navigate('/ielts-dashboard');
  };

  // Navigation
  const handleSectionChange = (idx) => {
    setCurrentSection(idx);
  };

  // Check if results are being shown (when apiResult exists with scores)
  const resultToUse = apiResult || apiResultProp;
  const hasAI = resultToUse ? hasAIScore(resultToUse) : false;
  // showResults is used for displaying results - includes test mode check
  const showResults = isTestMode || hasAI || (resultToUse && shouldShowScore(resultToUse));
  const hasResultsToShow = showResults && resultToUse;
  // For header button logic, don't include isTestMode - only check actual results
  const hasActualResults = hasAI || (resultToUse && shouldShowScore(resultToUse));

  // Close modal when results become available
  useEffect(() => {
    if (hasResultsToShow && endModalOpen) {
      setEndModalOpen(false);
    }
  }, [hasResultsToShow, endModalOpen]);

  return (
    <>
      <Global styles={ieltsPassageGlobalStyles} />
      <style>{writingTextareaStyles}</style>
      <TestContainer sx={{ 
        width: '100%', 
        margin: '0 auto', 
        boxSizing: 'border-box', 
        height: (isReviewMode || hasResultsToShow) ? 'auto' : '100vh', 
        minHeight: (isReviewMode || hasResultsToShow) ? 'unset' : '100vh',
        overflow: (isReviewMode || hasResultsToShow) ? 'visible' : 'hidden',
        overflowY: (isReviewMode || hasResultsToShow) ? 'auto' : 'hidden'
      }}>
        {/* EndModal for practice/simulation mode */}
      {!isTestMode && (
        <EndModal 
          open={endModalOpen} 
          onClose={() => setEndModalOpen(false)} 
          sentences={IELTS_WRITING_SENTENCES} 
          intervalMs={3000}
          testTitle="IELTS Writing Mock Test"
          navigateTo="/ielts-results"
        />
      )}
      {/* Show header only when NOT in review mode (review mode is for test details page) */}
      {!isReviewMode && (
        <IELTSHeader
          timerText={hasActualResults 
            ? `Test Completed - ${timer.minutes !== 'Unlimited' ? formatTime(timer.minutes * 60 + timer.seconds) : 'Unlimited'}`
            : (formatTime(timer.minutes === 'Unlimited' ? 'Unlimited' : timer.minutes * 60 + timer.seconds) + ' remaining')}
          showReview={!hasActualResults}
          showSaveExit={showSaveExit && !apiResult && !isTestMode}
          showSubmit={true}
          isSubmitting={isSubmitting}
          apiResult={hasActualResults ? apiResult : null}
          groupId={groupId}
          onReview={() => {}}
          onSaveExit={handleSaveAndExit}
          onSubmit={handleSubmit}
          onSubmitNavigate={hasActualResults && resultToUse?.response?.attemptId ? `/ielts-test-details?type=individual&attemptId=${resultToUse.response.attemptId}` : null}
          submitButtonText={hasActualResults ? 'View Results' : undefined}
          submitButtonDisabled={isSubmitting}
          onFullTestPause={!hasActualResults ? onFullTestPause : undefined}
        />
      )}

      <SubscriptionAiPromptDialog
        open={subscriptionPromptOpen}
        onClose={() => setSubscriptionPromptOpen(false)}
        skillName="Writing"
      />
      
      {/* Show results below header when available */}
      {showResults && resultToUse && (
        <>
          <IELTSBandScore 
            apiResult={resultToUse} 
            showCorrectIncorrect={false}
            showOnlyWhenScoreAvailable={true}
            preferManualScore={true}
          />
          {/* Show detailed scoring if AI scoring exists and status is completed (even if score is 0) */}
          {hasAIScore(resultToUse) && (
            <IELTSDetailedScoring
              aiScoring={getAIScoring(resultToUse)}
              testType="Writing"
            />
          )}
        </>
      )}
      {/* Bottom Navigation - above content for review mode (results page) */}
      {isReviewMode && sections.length > 1 && (
        <Box sx={{ width: '100%', mb: 2 }}>
          <IELTSBottomNavigation
            mode="task"
            sections={sections}
            currentSection={currentSection}
            onPartChange={(sectionIdx) => {
              setCurrentSection(sectionIdx);
            }}
            taskLabels={sections.reduce((acc, section, idx) => {
              acc[idx] = `Task ${idx + 1}`;
              return acc;
            }, {})}
            fixed={false}
          />
        </Box>
      )}
      {/* Draggable Split Content - Different layout for review mode */}
      {isTestMode ? (
        // Review/Test mode layout (no draggable divider, side by side)
        <Box ref={containerRef} sx={{ width: '100%', display: 'flex', flexDirection: 'column', minHeight: 0, boxSizing: 'border-box' }}>
          <Box sx={{ 
            display: 'flex', 
            gap: 2, 
            mb: 2, 
            flexDirection: { xs: 'column', md: 'row' },
            height: { xs: 'auto', md: '600px' },
            minHeight: { xs: 'auto', md: '600px' }
          }}>
            {/* Left Side - Passage */}
            <Box sx={{ flex: 1, minWidth: 0, height: { xs: '400px', md: '100%' }, display: 'flex', flexDirection: 'column' }}>
              <PassageContainer sx={{ flex: 1, minHeight: 0, height: '100%' }}>
                <style>{ieltsPassageInlineStyles}</style>
                <div className="ielts-passage-html" dangerouslySetInnerHTML={{ __html: sections && sections[currentSection]?.passage_text || '' }} />
              </PassageContainer>
            </Box>
            
            {/* Right Side - User Answer */}
            <Box sx={{ flex: 1, minWidth: 0, height: { xs: '400px', md: '100%' }, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <WritingQuestionsContainer sx={{ flex: 1, minHeight: 0, height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', pt: 1, px: 2, pb: 2 }}>
                <textarea
                  className="ielts-writing-textarea"
                  style={{
                    width: '100%',
                    maxWidth: '100%',
                    flex: 1,
                    minHeight: 0,
                    fontSize: '1rem',
                    padding: '12px',
                    border: isReviewMode ? '1px solid #E0E0E0' : '1px solid #E0E0E0',
                    borderRadius: '8px',
                    resize: 'none',
                    marginBottom: '8px',
                    color: '#333',
                    fontFamily: 'inherit',
                    boxSizing: 'border-box',
                    background: isReviewMode ? '#F5F5F5' : 'white',
                    display: 'block',
                    cursor: isReviewMode ? 'default' : 'text',
                    overflowY: 'auto',
                  }}
                  value={isReviewMode ? (userAnswers[currentSection] || '') : (answers[currentSection] || '')}
                  onChange={e => !isReviewMode && !hasResultsToShow && handleAnswerChange(currentSection, e.target.value)}
                  readOnly={isReviewMode || hasResultsToShow}
                  disabled={isReviewMode || hasResultsToShow}
                  placeholder={isReviewMode ? 'No response submitted' : "Type your answer here..."}
                />
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  sx={{ 
                    fontSize: '0.875rem',
                    flexShrink: 0,
                    marginBottom: '8px',
                    color: '#666'
                  }}
                >
                  Word Count: {countWords(isReviewMode ? (userAnswers[currentSection] || '') : (answers[currentSection] || ''))}
                </Typography>
                {isReviewMode && !userAnswers[currentSection] && (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    No response was submitted for this task.
                  </Typography>
                )}
              </WritingQuestionsContainer>
            </Box>
          </Box>
        </Box>
      ) : (
        // Practice mode layout (with draggable divider)
        <Box ref={containerRef} sx={{ 
          display: 'flex', 
          flex: 1, 
          height: hasResultsToShow ? 'auto' : '100%', 
          minHeight: hasResultsToShow ? 'unset' : 0, 
          width: '100%', 
          maxWidth: '100vw', 
          position: 'relative', 
          overflowX: 'auto', 
          overflowY: hasResultsToShow ? 'visible' : 'hidden',
          boxSizing: 'border-box', 
          marginBottom: sections.length > 1 ? '77px' : '0' 
        }}>
          {/* Left Side - Writing Prompt */}
          <Box sx={{ width: `${leftWidth}%`, minWidth: 0, height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxSizing: 'border-box' }}>
            <PassageContainer>
              <style>{ieltsPassageInlineStyles}</style>
              <div className="ielts-passage-html" dangerouslySetInnerHTML={{ __html: sections && sections[currentSection]?.passage_text || '' }} />
            </PassageContainer>
          </Box>
          {/* Draggable Divider */}
          <DraggableDivider dragging={dragging} onMouseDown={handleMouseDown} />
          {/* Right Side - Text Area */}
          <Box sx={{ flex: '1 1 0', minWidth: 0, width: '100%', maxWidth: '100%', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxSizing: 'border-box' }}>
            <WritingQuestionsContainer sx={{ width: '100%', maxWidth: '100%', minWidth: 0, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', boxSizing: 'border-box', p: 0, pl: 2, pr: 2, pt: 1, pb: sections.length > 1 ? '84px' : 2 }}>
              <textarea
                className="ielts-writing-textarea"
                style={{
                  width: '100%',
                  maxWidth: '100%',
                  flex: 1,
                  minHeight: hasResultsToShow ? '420px' : 0,
                  fontSize: '1rem',
                  padding: '12px',
                  border: hasResultsToShow ? '1px solid #E0E0E0' : '1px solid #E0E0E0',
                  borderRadius: '8px',
                  resize: 'none',
                  marginBottom: '8px',
                  color: '#333',
                  fontFamily: 'inherit',
                  boxSizing: 'border-box',
                  background: hasResultsToShow ? '#F5F5F5' : 'white',
                  display: 'block',
                  cursor: hasResultsToShow ? 'default' : 'text',
                  overflowY: 'auto',
                }}
                value={answers[currentSection] || ''}
                onChange={e => !hasResultsToShow && handleAnswerChange(currentSection, e.target.value)}
                readOnly={hasResultsToShow}
                disabled={hasResultsToShow}
                placeholder="Type your answer here..."
              />
              <Typography 
                variant="body2" 
                color="text.secondary" 
                sx={{ 
                  fontSize: '0.875rem',
                  flexShrink: 0,
                  marginBottom: 0,
                  color: '#666'
                }}
              >
                Word Count: {countWords(answers[currentSection] || '')}
              </Typography>
            </WritingQuestionsContainer>
          </Box>
        </Box>
      )}
      {/* Bottom Navigation for multiple tasks - at bottom for test mode and practice mode only (not review mode) */}
      {!isReviewMode && sections.length > 1 && (
        <IELTSBottomNavigation
          mode="task"
          sections={sections}
          currentSection={currentSection}
          onPartChange={(sectionIdx) => {
            // In task mode, onPartChange receives the section index directly
            setCurrentSection(sectionIdx);
          }}
          taskLabels={sections.reduce((acc, section, idx) => {
            acc[idx] = section.part_name || `Task ${idx + 1}`;
            return acc;
          }, {})}
          fixed={!isTestMode}
        />
      )}
      </TestContainer>
    </>
  );
};

export default WritingTestUI;
